import { Injectable, signal } from '@angular/core';
import { EErrorType } from '../components/error-page/error-page.component';
import { ContactDataResponse } from './validador-contacto.service';

export type AppState = 'loading' | 'otp' | 'success' | 'error';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly TOKEN_STORAGE_KEY = 'afp_token';
  private readonly CONTACT_DATA_STORAGE_KEY = 'afp_contact_data';

  private readonly _token = signal<string | null>(null);
  private readonly _contactData = signal<ContactDataResponse | null>(null);
  private readonly _isInitializing = signal<boolean>(true);
  private readonly _appState = signal<AppState>('loading');
  private readonly _clientApp = signal<'VRA'>('VRA');
  private readonly _redirectUrl = signal<string | null>(null);
  private readonly _errorType = signal<EErrorType>(EErrorType.NOT_FOUND);

  constructor() {
    this.loadFromStorage();
  }

  get token() {
    return this._token();
  }

  get contactData() {
    return this._contactData();
  }

  get isInitializing() {
    return this._isInitializing();
  }

  get appState() {
    return this._appState();
  }

  get redirectUrl() {
    return this._redirectUrl();
  }

  get errorType() {
    return this._errorType();
  }

  setToken(token: string) {
    this._token.set(token);
    this.saveToStorage();
  }

  setContactData(contactData: ContactDataResponse) {
    this._contactData.set(contactData);
    this.saveToStorage();
  }

  setInitializationComplete() {
    this._isInitializing.set(false);
  }

  setInitializationError() {
    this._isInitializing.set(false);
  }

  setAppState(state: AppState) {
    this._appState.set(state);
  }

  setSuccessState() {
    this._appState.set('success');
  }

  setErrorState(errorType: EErrorType = EErrorType.NOT_FOUND) {
    this._errorType.set(errorType);
    this._appState.set('error');
  }

  setOtpState() {
    this._appState.set('otp');
  }

  setRedirectUrl(url: string) {
    this._redirectUrl.set(url);
  }

  clearToken() {
    this._token.set(null);
    this.removeFromStorage();
  }

  clearContactData() {
    this._contactData.set(null);
    this.removeFromStorage();
  }

  hasToken(): boolean {
    return this._token() !== null;
  }

  hasContactData(): boolean {
    return this._contactData() !== null;
  }

  clearAll() {
    this.clearToken();
    this.clearContactData();
  }

  private saveToStorage(): void {
    try {
      if (this._token()) {
        sessionStorage.setItem(this.TOKEN_STORAGE_KEY, this._token()!);
      }
      if (this._contactData()) {
        sessionStorage.setItem(
          this.CONTACT_DATA_STORAGE_KEY,
          JSON.stringify(this._contactData()!)
        );
      }
    } catch (error) {
      console.warn('Error saving to sessionStorage:', error);
    }
  }

  private loadFromStorage(): void {
    try {
      const token = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
      if (token) {
        this._token.set(token);
      }

      const contactDataStr = sessionStorage.getItem(
        this.CONTACT_DATA_STORAGE_KEY
      );
      if (contactDataStr) {
        const contactData = JSON.parse(contactDataStr);
        this._contactData.set(contactData);
      }
    } catch (error) {
      console.warn('Error loading from sessionStorage:', error);
    }
  }

  private removeFromStorage(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(this.CONTACT_DATA_STORAGE_KEY);
    } catch (error) {
      console.warn('Error removing from sessionStorage:', error);
    }
  }

  hasPersistedData(): boolean {
    return this.hasToken() || this.hasContactData();
  }

  private setClientApp(app: 'VRA') {
    this._clientApp.set(app);
  }

  public verificacionContactoOk(data: ContactDataResponse): void {
    this.setContactData(data);
    this.setInitializationComplete();
    this.setOtpState();
    this.setClientApp('VRA');
  }

  public verificacionContactoError(): void {
    this.clearAll();
    this.setInitializationError();
    this.setErrorState(EErrorType.EXPIRED);
  }

  public verificacionContactoNotFound(): void {
    this.setInitializationError();
    this.setErrorState(EErrorType.NOT_FOUND);
  }
}
